import {create} from "zustand";
import {getSessionHeaders} from "../lib/session";

export type CatalogItem = {
    _id: string;
    name: string;
    price: number;
    image: string | null;
    imageUrl?: string;
    description?: string;
};

export type ApiResult = {
    success: boolean;
    message: string;
};

export type CatalogItemState = {
    items: CatalogItem[];
    isLoading: boolean;
    error: string | null;

    fetchCatalogItems: () => Promise<ApiResult>;
    resetCatalogItems: () => Promise<ApiResult>;
    createCatalogItem: (payload: Record<string, unknown>) => Promise<ApiResult>;
    updateCatalogItem: (id: string, payload: Record<string, unknown>) => Promise<ApiResult>;
    deleteCatalogItem: (id: string) => Promise<ApiResult>;
};

const API_URL = "/api/catalog-items";

const parseErrorMessage = async (res: Response): Promise<string> => {
    try {
        const data = (await res.json()) as { message?: unknown };
        if (typeof data?.message === "string" && data.message.trim()) {
            return data.message;
        }
    } catch {
        // ignore
    }
    return `${res.status} ${res.statusText}`.trim();
};

const buildUpsertFormData = (payload: Record<string, unknown>): FormData => {
    const fd = new FormData();

    for (const [k, v] of Object.entries(payload)) {
        if (v === undefined || v === null) continue;

        if (v instanceof File) {
            fd.append(k, v);
            continue;
        }

        fd.append(k, typeof v === "string" ? v : String(v));
    }

    return fd;
};

export const useCatalogItemStore = create<CatalogItemState>((set) => ({
    items: [],
    isLoading: false,
    error: null,

    fetchCatalogItems: async () => {
        set({isLoading: true, error: null});
        try {
            const res = await fetch(API_URL, {headers: getSessionHeaders()});

            if (!res.ok) {
                const message = await parseErrorMessage(res);
                set({error: message, isLoading: false});
                return {success: false, message};
            }

            const items = (await res.json()) as CatalogItem[];
            set({items, isLoading: false, error: null});
            return {success: true, message: "Fetched catalog items"};
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "Unknown error";
            set({error: message, isLoading: false});
            return {success: false, message};
        }
    },

    resetCatalogItems: async () => {
        set({isLoading: true, error: null});
        try {
            const res = await fetch(`${API_URL}/reset`, {
                method: "POST",
                headers: getSessionHeaders(),
            });

            if (!res.ok) {
                const message = await parseErrorMessage(res);
                set({error: message, isLoading: false});
                return {success: false, message};
            }

            const items = (await res.json()) as CatalogItem[];
            set({items, isLoading: false, error: null});
            return {success: true, message: "Demo catalog reset"};
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "Unknown error";
            set({error: message, isLoading: false});
            return {success: false, message};
        }
    },

    createCatalogItem: async (payload) => {
        set({isLoading: true, error: null});
        try {
            const res = await fetch(API_URL, {
                method: "POST",
                headers: getSessionHeaders(),
                body: buildUpsertFormData(payload),
            });

            if (!res.ok) {
                const message = await parseErrorMessage(res);
                set({error: message, isLoading: false});
                return {success: false, message};
            }

            const created = (await res.json()) as CatalogItem;

            set((state) => ({
                items: [created, ...state.items],
                isLoading: false,
                error: null,
            }));

            return {success: true, message: "Catalog item created"};
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "Unknown error";
            set({error: message, isLoading: false});
            return {success: false, message};
        }
    },

    deleteCatalogItem: async (id) => {
        try {
            const res = await fetch(`${API_URL}/${id}`, {
                method: "DELETE",
                headers: getSessionHeaders(),
            });

            if (!res.ok) {
                const message = await parseErrorMessage(res);
                return {success: false, message};
            }

            set((state) => ({
                items: state.items.filter((i) => i._id !== id),
            }));

            return {success: true, message: "Catalog item deleted"};
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "Unknown error";
            return {success: false, message};
        }
    },

    updateCatalogItem: async (id, payload) => {
        try {
            const res = await fetch(`${API_URL}/${id}`, {
                method: "PUT",
                headers: getSessionHeaders(),
                body: buildUpsertFormData(payload),
            });

            if (!res.ok) {
                const message = await parseErrorMessage(res);
                return {success: false, message};
            }

            const updated = (await res.json()) as CatalogItem;

            set((state) => ({
                items: state.items.map((i) => (i._id === id ? updated : i)),
            }));

            return {success: true, message: "Catalog item updated"};
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "Unknown error";
            return {success: false, message};
        }
    },
}));